import { defineStore } from 'pinia'

// Role hierarchy and permissions
const roleHierarchy = {
  admin: ['admin', 'manager', 'tester', 'viewer'],
  manager: ['manager', 'tester', 'viewer'],
  tester: ['tester', 'viewer'],
  viewer: ['viewer']
}

const rolePermissions = {
  admin: ['create', 'read', 'update', 'delete', 'manage_users'],
  manager: ['create', 'read', 'update', 'manage_team'],
  tester: ['create', 'read', 'update'],
  viewer: ['read']
}

export const useAuthStore = defineStore('auth', {
  state: () => {
    return {
      user: null,
      subscription: null,
      initialized: false
    }
  },

  getters: {
    isAuthenticated: (state) => state.initialized && !!state.user?.token,
    currentUser: (state) => state.user,
    userInitials: (state) => {
      if (state.user?.firstName && state.user?.lastName) {
        return `${state.user.firstName[0]}${state.user.lastName[0]}`
      }
      return (state.user?.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase()
    },
    userName: (state) => {
      if (state.user?.firstName && state.user?.lastName) {
        return `${state.user.firstName} ${state.user.lastName}`
      }
      return state.user?.email?.split('@')[0] || 'User'
    },
    organization: (state) => state.user?.organization,
    organizationSlug: (state) => state.user?.organization?.slug,
    // Role-based getters
    isAdmin: (state) => state.user?.role === 'admin',
    isManager: (state) => state.user?.role === 'manager',
    isTester: (state) => state.user?.role === 'tester',
    isViewer: (state) => state.user?.role === 'viewer',
    // Subscription getters
    hasActiveSubscription: (state) => state.subscription?.status === 'active' || state.subscription?.status === 'trialing',
    isOnTrial: (state) => state.subscription?.status === 'trialing',
    subscriptionStatus: (state) => state.subscription?.status,
    subscriptionPlan: (state) => state.subscription?.plan,
  },

  actions: {
    initialize() {
      if (this.initialized) return;
      
      try {
        const savedUser = localStorage.getItem('user')
        if (savedUser) {
          const userData = JSON.parse(savedUser)
          if (userData && userData.token) {
            this.user = userData
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth store:', error)
      } finally {
        this.initialized = true
      }
    },

    setUser(userData) {
      this.user = userData
      localStorage.setItem('user', JSON.stringify(userData))
    },

    clearAuth() {
      this.user = null
      localStorage.removeItem('user')
    },

    hasRole(role) {
      if (!this.user) {
        console.warn('hasRole called with no user in authStore');
        return false;
      }
      
      if (!this.user.role) {
        console.warn(`User has no role defined. User: ${JSON.stringify(this.user)}`);
        return false;
      }
      
      // In case of admin, always return true regardless of hierarchy
      if (this.user.role === 'admin') {
        return true;
      }
      
      return roleHierarchy[this.user.role]?.includes(role) || false;
    },

    hasPermission(permission) {
      return rolePermissions[this.user?.role]?.includes(permission) || false
    },

    async fetchSubscription() {
      if (!this.user?.token) return
      
      try {
        const { default: subscriptionService } = await import('@/services/subscription')
        const data = await subscriptionService.getSubscriptionStatus()
        this.subscription = data.subscription
      } catch (error) {
        console.error('Failed to fetch subscription:', error)
      }
    },

    setSubscription(subscriptionData) {
      this.subscription = subscriptionData
    }
  }
}) 